import { useState } from "react";
import HomeIcon from "@/assets/svgs/home";
import StudentIcon from "@/assets/svgs/student";
import AnnouncementIcon from "@quest/assets/svgs/announcement";
import ResultIcon from "@quest/assets/svgs/result";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/tooltip";
import { FileUp, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import SchoolLogo from "./header-logo";
import User from "@/assets/svgs/user";
import Assignments from "@/assets/svgs/assignments";
import { usePermissions } from "@quest/hooks/permissions/use-permissions";
import { Icon } from "@iconify/react/dist/iconify.js";
import FoldersIcon from "@quest/assets/svgs/folders";
import { useAppSelector } from "@quest/hooks";
import IcaresButton from "@quest/assets/images/favicon.png";
import { ROLES } from "@/libs/constants/roles";

function AdminNavigationBar() {
  const { t } = useTranslation("Home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { canRead } = usePermissions();
  const canReadAssignments = canRead("curriculum");

  const canReadAnnouncements = canRead("announcement");

  const canReadDocuments = canRead("student-documents");

  const canReadFolders = canRead("folders");

  const canReadSubjects = canRead("subjects");

  const canManageIslandTimeSlots = canRead("island-management");

  const canReadStudents = canRead("students");

  const canReadIcaresTestAccessibility = canRead("icares-test-accessibility");

  const { role } = useAppSelector((state) => state.auth);
  const isTenantAdmin = role === ROLES.TENANT_ADMIN;
  const isTeacher = role === ROLES.TEACHER;

  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate("/admin");
  };

  const menuItems = [
    { id: 1, logo: <HomeIcon />, href: "/admin", name: t("home") },
    {
      id: 2,
      logo: <Icon icon="uil:statistics" width="24" height="24" />,
      href: "/admin/statistics",
      name: t("statistics"),
    },
    ...(isTeacher
      ? [
          {
            id: 21,
            logo: <Icon icon="mdi:account-tie" width="24" height="24" />,
            href: "/admin/mentor-dashboard",
            name: t("mentorDashboard"),
          },
        ]
      : []),
    ...(isTenantAdmin
      ? [
          {
            id: 22,
            logo: (
              <Icon icon="mdi:account-group-outline" width="24" height="24" />
            ),
            href: "/admin/mentor-overview",
            name: t("mentorOverview"),
          },
        ]
      : []),

    ...(canReadStudents
      ? [
          {
            id: 3,
            logo: <StudentIcon />,
            href: "/admin/students",
            name: t("Students:students"),
          },
        ]
      : []),
    ...(canReadAssignments
      ? [
          {
            id: 4,
            logo: <Assignments />,
            href: "/admin/quest-management",
            name: t("assignments"),
          },
        ]
      : []),
    {
      id: 5,
      logo: <ResultIcon />,
      href: "/admin/assignment-results",
      name: t("Common:assignmentResults"),
    },
    ...(canReadAnnouncements
      ? [
          {
            id: 6,
            logo: <AnnouncementIcon />,
            href: "/admin/announcements",
            name: t("Home:announcements"),
          },
        ]
      : []),
    // ...(canReadReminders
    //   ? [
    //       {
    //         id: 6,
    //         logo: (
    //           <Icon icon="fluent-mdl2:reminder-time" width="24" height="24" />
    //         ),
    //         href: "/admin/reminders",
    //         name: t("Home:reminders"),
    //       },
    //     ]
    //   : []),

    ...(canReadSubjects
      ? [
          {
            id: 7,
            logo: <Icon icon="lucide:notebook-tabs" width="24" height="24" />,
            href: "/admin/subjects",
            name: t("Subjects:subjects"),
          },
        ]
      : []),
    ...(canReadDocuments
      ? [
          {
            id: 8,
            logo: <FileUp />,
            href: "/admin/documents",
            name: t("DocumentUpload:documents"),
          },
        ]
      : []),

    ...(canReadFolders
      ? [
          {
            id: 9,
            logo: <FoldersIcon />,
            href: "/admin/folders",
            name: t("Folders:folders"),
          },
        ]
      : []),

    ...(canManageIslandTimeSlots
      ? [
          {
            id: 10,
            logo: <Icon icon="entypo:time-slot" width="24" height="24" />,
            href: "/admin/time-slots?tab=overideList",
            name: t("TimeSlots:accessibilityManagement"),
          },
        ]
      : []),

    ...(canReadIcaresTestAccessibility
      ? [
          {
            id: 11,
            logo: (
              <img
                src={IcaresButton}
                alt="icares-button"
                className="w-[25px] h-[25px] object-contain "
              />
            ),
            href: "/admin/manage-icares-tests",
            name: t("ICaresTest:icaresTestManagementTitle"),
          },
        ]
      : []),

    {
      id: 12,
      logo: <User />,
      href: "/admin/profile",
      name: t("Common:profile"),
    },
  ];

  const isActive = (href: string) => {
    return window.location.pathname === href.split("?")[0];
  };

  return (
    <div className="pt-[2.5rem]">
      <div className="hidden xl:flex justify-between col-span-1">
        <SchoolLogo handleClick={handleLogoClick} />
        <div className="flex justify-between items-center gap-4">
          <div
            id="navigation-menu"
            className="bg-[#f49b4b]  p-2 w-fit flex justify-center items-center gap-[24px] h-fit rounded-[100px]"
          >
            {menuItems.map((item) => (
              <TooltipProvider key={item.id}>
                <Tooltip>
                  <TooltipTrigger>
                    <NavLink
                      to={item.href}
                      className={`flex items-center gap-2 p-4 cursor-pointer hover:bg-[#FFD1A5] hover:rounded-[100px] transition-all duration-300 ease-in-out ${
                        isActive(item.href)
                          ? "bg-[#FFD1A5] rounded-[100px]"
                          : ""
                      }`}
                    >
                      {item.logo}
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-center text-[12px] font-bold">
                      {item.name}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Hamburger Menu (Tablet and below) */}
      <div className="xl:hidden fixed top-4 left-4 z-50">
        <button
          data-testid="mobile-menu-button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="  bg-white p-2 rounded-md shadow-md"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Dropdown Drawer */}
      {isMenuOpen && (
        <div className="fixed overflow-auto h-full top-0 left-0 w-fit  overflow-y-auto bg-[#f49b4b]  z-40 p-6 flex flex-col gap-6 shadow-lg">
          <SchoolLogo handleClick={handleLogoClick} />
          {menuItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.href}
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 p-3 hover:bg-[#FFD1A5] rounded-md "
            >
              {item.logo}
              <span className="font-bold">{item.name}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminNavigationBar;
